import { Type } from '@angular/core';
import moment from 'moment';
import { DASH, UserSettings } from '@miq/utils';
import { PropertyExtractionMode, SummaryElement } from '@miq/shared-models';
import { Step, StepComponent, WizardDef } from './model/wizard.model';

/**
 * L2-S1/T6: the PURE half of the host's
 * `src/app/efficiencies/availability/stacking-plan-availability-wizard-workflow.config.ts`.
 *
 * That file was the single most expensive seam the reports remote still had: it value-imports the
 * three wizard STEP COMPONENT CLASSES, and one of them (`AddAvailabilityComponent`) pulls ~1189
 * host files behind it, holding the union of every sanctioned seam's closure at 1238. The wizard
 * engine cannot lazy-load a step — `wizard.component.ts#loadStep` calls
 * `viewContainerRef.createComponent(component.type)` synchronously — so the classes cannot be
 * dropped, only INVERTED: the shell that owns them supplies them through
 * `AVAILABILITY_WIZARD_STEPS` (@miq/platform tokens) and the wizard definition is BUILT from that.
 *
 * Everything here is data + translation keys, so host and remote share one copy and cannot drift.
 *
 * ------------------------------------------------------------------------------------------------
 * WHY THESE ARE FACTORIES AND NOT FROZEN CONSTANTS
 *
 * The host previously exported `NewAvailabilityWizard` as a module-level `Object.freeze({...})` and
 * had `NewReportAvailabilityWizard()` ALIAS the very same `Step` objects and then mutate them:
 *
 *     const [selectProperty, requiredDetails, showSummary] = [SelectPropertyStep, RequiredDetailsStepNew, ShowSummaryStep];
 *     requiredDetails.prev = 'step0_SelectProperty';
 *     requiredDetails.stepNumber = 2;
 *     showSummary.stepNumber = 3;
 *
 * `Object.freeze` is SHALLOW, so the frozen `NewAvailabilityWizard.steps` pointed at those same
 * mutable objects. Opening the REPORT availability wizard therefore permanently renumbered the
 * stacking-plan wizard's steps (1,2 -> 2,3) and gave its first step a `prev` pointing at
 * `step0_SelectProperty`, a step that does not exist in that wizard. Building both wizards from
 * fresh step objects on every call removes the aliasing entirely; there is nothing left to mutate.
 * ------------------------------------------------------------------------------------------------
 */

/**
 * The step component classes the availability wizards render. Supplied by the shell (see
 * `AVAILABILITY_WIZARD_STEPS` in @miq/platform) rather than imported, so this library — and the
 * reports remote — never take a module edge on the host's `@efficiencies` component tree.
 */
export interface AvailabilityWizardSteps {
  /** `@efficiencies/select-property-container/select-property-container.component` */
  selectProperty: Type<unknown>;
  /** `@efficiencies/availability/add-availability/add-availability.component` */
  addAvailability: Type<unknown>;
  /** `@efficiencies/availability/select-availability/select-availability.component` */
  selectAvailability: Type<unknown>;
}

/**
 * Mirrors `formatDate` from @miq/stacking-plan (`lib/shared/utils/functions.ts`), which this
 * library cannot import: @miq/stacking-plan is built AFTER @miq/platform-ui (see the
 * `build:libraries` npm script, which excludes it from the run-many and builds it last), so the
 * dependency can only run the other way.
 *
 * ONE DELIBERATE DIFFERENCE, and it is a fix rather than a regression: @miq/stacking-plan resolves
 * the output format ONCE, at module-eval time, via `MY_FORMATS = UserSettings.getGlobalDateDisplayFormat('value')`
 * in `lib/shared/constants/date.const.ts`, used as a default parameter. Here it is resolved PER
 * CALL, matching what `MomentTimezonePipe` and `DateWithFormatPipe` in this library already do, so
 * a user who changes their date preference mid-session sees it applied. Both read the same
 * `USER_SETTINGS` localStorage entry through the same `UserSettings` helper, so for a session with
 * a stable preference the two are byte-identical.
 */
const UTC_EDP_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
const formatDate = (date: string | Date): string =>
  date ? moment.utc(date, UTC_EDP_INPUT_FORMAT).format(UserSettings.getGlobalDateDisplayFormat('value')) : null;

/** Rows of the availability wizard's summary step. Pure display config. */
export const AVAILABILITY_SUMMARY_ELEMENTS: { [key: string]: SummaryElement } = {
  floorSuite: {
    label: 'recordsSearch.efficienciesWizards.floorSuite',
    displayFnArgument: {
      displayFn: ({ floor_suite }) => {
        return `${floor_suite.floor_number}| ${floor_suite.suite_number}`;
      },
    },
  },
  floorSuites: {
    label: 'recordsSearch.efficienciesWizards.floorSuites',
    displayFnArgument: {
      displayFn: ({ floor_suites }) => {
        return `${floor_suites[0].floor_number} | ${floor_suites[0].suite_number}`;
      },
    },
  },
  availabilitySpace: {
    label: 'recordsSearch.efficienciesWizards.availabilitySpace',
    displayFnArgument: { displayFn: ({ available_space }) => (available_space ? available_space : ' - ') },
  },
  availabilityType: {
    label: 'recordsSearch.efficienciesWizards.availabilityType',
    field: 'ref_availability_type_desc',
    displayFnArgument: {
      displayFn: ({ ref_availability_type_desc }) => (ref_availability_type_desc ? ref_availability_type_desc : DASH),
    },
  },
  dateAvailable: {
    label: 'recordsSearch.efficienciesWizards.dateAvailable',
    displayFnArgument: {
      displayFn: ({ date_available }) => (date_available ? formatDate(date_available) : ' - '),
    },
  },
  dateListed: {
    label: 'recordsSearch.efficienciesWizards.dateListed',
    displayFnArgument: {
      displayFn: ({ date_on_market }) => (date_on_market ? formatDate(date_on_market) : ' - '),
    },
  },
  leaseType: {
    label: 'recordsSearch.efficienciesWizards.leaseType',
    field: 'ref_lease_type_desc',
    displayFnArgument: {
      displayFn: ({ ref_lease_type_desc }) => (ref_lease_type_desc ? ref_lease_type_desc : DASH),
    },
  },
  propertyType: {
    label: 'recordsSearch.efficienciesWizards.propertyType',
    displayFnArgument: {
      displayFn: ({ property_usage_id }, _, mapLOV) => {
        const type = (mapLOV as unknown as { id: unknown; value: string }[]).find(types => types.id === property_usage_id);
        return type ? type.value : DASH;
      },
    },
  },
};

/** Freshly built "select a property" step. Only the 3-step report wizard uses it. */
const makeSelectPropertyStep = (type: Type<any>): Step<StepComponent> => ({
  name: 'recordsSearch.selectProperty',
  stepNumber: 1,
  component: {
    type,
    args: {
      options: {
        propertyTypesCatalog: {},
        propertyTypeRequired: true,
        propertyExtractionMode: PropertyExtractionMode.id,
        selectedUsageType: '',
      },
    },
  },
  targetData: { propertyId: true, propertyTypeId: true },
  next: 'step1_RequiredDetails',
});

/**
 * Freshly built "required details" step. `stepNumber` differs per wizard, and `prev` is set ONLY
 * for the 3-step report wizard — in the host's 2-step wizard this is the first step and the key
 * must be absent, exactly as in the original module-level object.
 */
const makeRequiredDetailsStep = (type: Type<any>, stepNumber: number, prev?: string): Step<StepComponent> => {
  const step: Step<StepComponent> = {
    name: 'recordsSearch.efficienciesWizards.requiredDetails',
    stepNumber,
    component: {
      type,
      args: {
        options: {
          propertyTypesCatalog: {},
        },
      },
    },
    targetData: {
      createUpdatePayload: true,
      spaceValidationMsg: true,
    },
    next: 'step2_ShowSummary',
  };
  if (prev) {
    step.prev = prev;
  }
  return step;
};

/** Freshly built summary step. `stepNumber` differs per wizard. */
const makeShowSummaryStep = (type: Type<any>, stepNumber: number): Step<StepComponent> => ({
  name: 'recordsSearch.efficienciesWizards.summary',
  stepNumber,
  component: {
    type,
    args: {
      options: {
        targetCompEntity: 'availability',
        showSummary: true,
        summaryElements: [...Object.values(AVAILABILITY_SUMMARY_ELEMENTS)],
        summaryDescription: 'recordsSearch.efficienciesWizards.spWorkflowSelectTimDesc',
        propertyTypesCatalog: {},
      },
    },
  },
  targetData: {},
  prev: 'step1_RequiredDetails',
});

const NEW_AVAILABILITY_TITLE = 'recordsSearch.efficienciesWizards.createNewAvailability';

/**
 * The host's stacking-plan wizard: 2 steps, numbered 1 and 2, no property selection (the stacking
 * plan already knows the property). `step1_RequiredDetails` has NO `prev` — it is the first step.
 */
export function buildNewAvailabilityWizard(
  steps: Pick<AvailabilityWizardSteps, 'addAvailability' | 'selectAvailability'>
): WizardDef {
  return {
    title: NEW_AVAILABILITY_TITLE,
    steps: {
      step1_RequiredDetails: makeRequiredDetailsStep(steps.addAvailability, 1),
      step2_ShowSummary: makeShowSummaryStep(steps.selectAvailability, 2),
    },
    data: {},
  };
}

/**
 * The reports remote's wizard: the same two steps preceded by a property picker, so the numbering
 * shifts to 1/2/3 and `step1_RequiredDetails` gains `prev: 'step0_SelectProperty'`. Built fresh on
 * every call, so it can never renumber {@link buildNewAvailabilityWizard}'s steps.
 */
export function buildNewReportAvailabilityWizard(steps: AvailabilityWizardSteps): WizardDef {
  return {
    title: NEW_AVAILABILITY_TITLE,
    steps: {
      step0_SelectProperty: makeSelectPropertyStep(steps.selectProperty),
      step1_RequiredDetails: makeRequiredDetailsStep(steps.addAvailability, 2, 'step0_SelectProperty'),
      step2_ShowSummary: makeShowSummaryStep(steps.selectAvailability, 3),
    },
    data: {},
  };
}
